import UPButton from 'components/button';
import ErrorBar from 'components/error-bar';
import UPSidePanel, { UPSidePanelSection } from 'components/side-panel';
import { CompanyRole } from 'constants/CompanyRole';
import {
  hasValueChanged,
  isSwitchableKind,
  pickUnitFields,
  validateUnitValue,
} from 'modules/management/time-registration-policy/helpers';
import PolicyUnitFields from 'modules/management/time-registration-policy/PolicyUnitFields';
import { PanelErrorRow, PanelExplanation, PanelFields } from 'modules/management/time-registration-policy/styles';
import { getUnitDefinition, POLICY_SECTIONS } from 'modules/management/time-registration-policy/units';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import {
  getCompanyTimeRegistrationPolicyAction,
  saveCompanyPolicyUnitAction,
} from 'store/actions/company-time-registration-policy-actions';
import { companyPolicyAgencyNameSelector } from 'store/selectors/company-time-registration-policy-selectors';
import {
  GENERAL__CLOSE,
  GENERAL__SAVE_BUTTON,
  TIME_REGISTRATION_POLICY__CONCURRENT_CHANGE,
  TIME_REGISTRATION_POLICY__PANEL_GROUP,
  TIME_REGISTRATION_POLICY__RELOAD,
  TIME_REGISTRATION_POLICY__SAVE_FAILED,
  TIME_REGISTRATION_POLICY__SAVE_TIMEOUT,
  TIME_REGISTRATION_SETTINGS__COLUMN_VALUE,
  TIME_REGISTRATION_SETTINGS__COMPANY_DEFAULT,
  TIME_REGISTRATION_SETTINGS__LOCATION_OVERRIDES,
  TIME_REGISTRATION_SETTINGS__PANEL_STATE,
  TIME_REGISTRATION_SETTINGS__REVERT_PENDING,
  TIME_REGISTRATION_SETTINGS__REVERT_TO_AGENCY,
} from 'translations/constants';
import {
  CompanyPolicyLocation,
  CompanyPolicySaveResult,
  CompanyUnitSetting,
  CompanyUnitState,
  LocationOverride,
  OverrideErrors,
} from 'types/company/time-registration/ICompanyTimeRegistrationPolicy';
import { FieldErrors, UnitValue, UnitValueField } from 'types/time-registration-policy/ITimeRegistrationPolicy';
import { renderUnitStatus } from './columns';
import {
  canEditUnit,
  getUnitStatus,
  hasLocationOverrides,
  hasOverrideErrors,
  haveOverridesChanged,
  STATUS_HELP_KEYS,
  toSetting,
  UNIT_LOCKED_KEYS,
  validateOverrides,
} from './helpers';
import LocationOverridesEditor from './LocationOverridesEditor';
import { StateBlock, StateHelp } from './styles';

interface CompanyUnitPanelProps {
  companyId: number;
  state: CompanyUnitState;
  locations: CompanyPolicyLocation[];
  role: CompanyRole;
  onClose: () => void;
  onSaved: (name: string) => void;
}

const CompanyUnitPanel = ({
  companyId,
  state,
  locations,
  role,
  onClose,
  onSaved,
}: CompanyUnitPanelProps): JSX.Element => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const agencyName = useAppSelector(companyPolicyAgencyNameSelector);
  const def = getUnitDefinition(state.unit);
  const sectionTitleKey = POLICY_SECTIONS.find(section => section.section === def.section)?.titleKey;
  const editable = canEditUnit(state, role);
  const withOverrides = hasLocationOverrides(def);
  const status = getUnitStatus(state);
  const storedOverrides = state.locationOverrides ?? [];

  const [draft, setDraft] = useState<UnitValue>(pickUnitFields(def, state.value));
  const [overrides, setOverrides] = useState<LocationOverride[]>(storedOverrides);
  const [revertPending, setRevertPending] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [overrideErrors, setOverrideErrors] = useState<OverrideErrors[]>([]);
  const [alert, setAlert] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(pickUnitFields(def, state.value));
    setOverrides(state.locationOverrides ?? []);
    setRevertPending(false);
    setErrors({});
    setOverrideErrors([]);
  }, [def, state]);

  const dirty =
    revertPending ||
    hasValueChanged(def, state.value, draft) ||
    (withOverrides && haveOverridesChanged(def, storedOverrides, overrides));

  const onFieldChange = (patch: Partial<UnitValue>) => {
    setDraft(prev => ({ ...prev, ...patch }));
    setErrors(prev => {
      const next = { ...prev };
      (Object.keys(patch) as UnitValueField[]).forEach(field => delete next[field]);
      return next;
    });
    setAlert(null);
  };

  const onOverridesChange = (next: LocationOverride[]) => {
    setOverrides(next);
    setOverrideErrors([]);
    setAlert(null);
  };

  const onRevert = () => {
    setRevertPending(true);
    setDraft(pickUnitFields(def, state.value));
    setErrors({});
    setAlert(null);
  };

  const buildProposed = (): CompanyUnitSetting => {
    const locationOverrides = withOverrides ? overrides : storedOverrides;
    if (revertPending) return { value: state.value, overruled: false, locationOverrides };
    const keepStoredSubFields = isSwitchableKind(def.kind) && !draft.enabled;
    const value = keepStoredSubFields ? { ...pickUnitFields(def, state.value), enabled: false } : draft;
    return { value, overruled: state.overruled || hasValueChanged(def, state.value, draft), locationOverrides };
  };

  const showFailure = (result: CompanyPolicySaveResult) => {
    switch (result.status) {
      case 'conflict':
        setConflict(true);
        setAlert(t(TIME_REGISTRATION_POLICY__CONCURRENT_CHANGE));
        break;
      case 'locked':
        setAlert(t(UNIT_LOCKED_KEYS[state.unit], { agencyName }));
        dispatch(getCompanyTimeRegistrationPolicyAction(companyId));
        break;
      case 'timeout':
        setAlert(t(TIME_REGISTRATION_POLICY__SAVE_TIMEOUT));
        break;
      case 'invalid': {
        setErrors(result.fieldErrors);
        const visibleFields =
          isSwitchableKind(def.kind) && !draft.enabled ? ['enabled'] : Object.keys(pickUnitFields(def, draft));
        const hiddenError = Object.entries(result.fieldErrors).find(([field]) => !visibleFields.includes(field))?.[1];
        if (hiddenError) setAlert(t(hiddenError.key, hiddenError.params));
        break;
      }
      case 'error':
        setAlert(t(result.messageKey ?? TIME_REGISTRATION_POLICY__SAVE_FAILED));
        break;
      default:
        setAlert(t(TIME_REGISTRATION_POLICY__SAVE_FAILED));
    }
  };

  const onSave = async () => {
    if (saving || !dirty) return;
    const valueErrors = revertPending ? {} : validateUnitValue(def, draft);
    const nextOverrideErrors = withOverrides ? validateOverrides(def, overrides) : [];
    if (Object.keys(valueErrors).length || hasOverrideErrors(nextOverrideErrors)) {
      setErrors(valueErrors);
      setOverrideErrors(nextOverrideErrors);
      return;
    }
    setSaving(true);
    setAlert(null);
    setConflict(false);
    const result = await dispatch(
      saveCompanyPolicyUnitAction({
        companyId,
        unit: state.unit,
        expected: toSetting(state),
        proposed: buildProposed(),
      }),
    ).unwrap();
    setSaving(false);
    if (result.status === 'success') {
      onSaved(t(def.nameKey));
      onClose();
      return;
    }
    showFailure(result);
  };

  const reloadAfterConflict = async () => {
    await dispatch(getCompanyTimeRegistrationPolicyAction(companyId));
    setConflict(false);
    setAlert(null);
  };

  const close = () => {
    if (!saving) onClose();
  };

  return (
    <UPSidePanel
      open
      title={t(def.nameKey)}
      subtitle={t(TIME_REGISTRATION_POLICY__PANEL_GROUP, { section: t(sectionTitleKey) })}
      onClose={close}
      testId="tr-company-panel"
      footer={
        <>
          {editable && (
            <UPButton
              text={t(GENERAL__SAVE_BUTTON)}
              onClick={onSave}
              disabled={saving || !dirty}
              testId="tr-company-panel-save"
            />
          )}
          <UPButton text={t(GENERAL__CLOSE)} outlined onClick={close} testId="tr-company-panel-close" />
        </>
      }
    >
      {alert && (
        <PanelErrorRow data-testid="tr-company-panel-alert">
          <ErrorBar message={alert} />
          {conflict && (
            <UPButton
              text={t(TIME_REGISTRATION_POLICY__RELOAD)}
              outlined
              onClick={reloadAfterConflict}
              testId="tr-company-panel-reload"
            />
          )}
        </PanelErrorRow>
      )}
      <UPSidePanelSection
        title={t(
          withOverrides ? TIME_REGISTRATION_SETTINGS__COMPANY_DEFAULT : TIME_REGISTRATION_SETTINGS__COLUMN_VALUE,
        )}
        testId="tr-company-panel-value"
      >
        <PanelExplanation data-testid="tr-company-panel-explanation">{t(def.descKey)}</PanelExplanation>
        <PanelFields>
          <PolicyUnitFields
            def={def}
            value={draft}
            errors={errors}
            onChange={onFieldChange}
            disabled={!editable || revertPending}
          />
        </PanelFields>
      </UPSidePanelSection>
      {withOverrides && (
        <UPSidePanelSection
          title={t(TIME_REGISTRATION_SETTINGS__LOCATION_OVERRIDES)}
          testId="tr-company-panel-overrides"
        >
          <LocationOverridesEditor
            def={def}
            overrides={overrides}
            locations={locations}
            errors={overrideErrors}
            companyDefault={draft}
            disabled={!editable}
            onChange={onOverridesChange}
          />
        </UPSidePanelSection>
      )}
      <UPSidePanelSection title={t(TIME_REGISTRATION_SETTINGS__PANEL_STATE)} testId="tr-company-panel-state">
        <StateBlock>
          <span data-testid="tr-company-panel-status">{renderUnitStatus(status, t)}</span>
          <StateHelp>{t(STATUS_HELP_KEYS[status], { agencyName })}</StateHelp>
          {revertPending && (
            <StateHelp data-testid="tr-company-panel-revert-pending">
              {t(TIME_REGISTRATION_SETTINGS__REVERT_PENDING, { agencyName })}
            </StateHelp>
          )}
          {editable && state.overruled && !revertPending && (
            <UPButton
              text={t(TIME_REGISTRATION_SETTINGS__REVERT_TO_AGENCY, { agencyName })}
              outlined
              onClick={onRevert}
              testId="tr-company-panel-revert"
            />
          )}
        </StateBlock>
      </UPSidePanelSection>
    </UPSidePanel>
  );
};

export default CompanyUnitPanel;
